import React, { useEffect, useState } from "react";
import { Table, Form, Button, Row, Col, Badge, Alert } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useGetCRAByIdQuery,
  useUpdateCRAMutation,
  useSendToValidatorMutation,
} from "store/cra/cra.api";
import { useGetProjectsByCollaboratorQuery } from "store/project/project.api";
import axios from "axios";
import Validations from "@pages/gestionConge/Validations/Validations";

const EditCra = () => {
  const { craId } = useParams();
  const currentUser = useSelector((state) => state.auth.user);
  const { data: craData, isLoading, isError } = useGetCRAByIdQuery(craId);
  const [updateCRA, { isLoading: isSaving }] = useUpdateCRAMutation();
  const [sendToValidator, { isLoading: sendingValidator }] =
    useSendToValidatorMutation();

  const { data: projectsData } = useGetProjectsByCollaboratorQuery(
    currentUser._id
  );
  const projects = projectsData?.data?.projects || [];

  const [daysInMonth, setDaysInMonth] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [lineData, setLineData] = useState({});
  const [lineDisabled, setLineDisabled] = useState({});
  const [commentaire, setCommentaire] = useState("");
  const [files, setFiles] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isEnvoyer, setIsEnvoyer] = useState(false);
  const weekDays = ["DI", "LU", "MA", "ME", "JE", "VE", "SA"];

  const currentMonth = craData?.data?.data?.month - 1;
  const currentYear = craData?.data?.data?.year;
  const [steps, setSteps] = useState([
    {
      name: currentUser.name,
      date: craData?.data?.data?.createdAt
        ? new Date(craData?.data?.data?.createdAt)
        : null,
      signed: true,
    },
    {
      name: craData?.data?.data?.validateur?.name,
      date: null,

      signed: craData?.data?.data?.status === "refused",
    },
  ]);
  // Init CRA
  useEffect(() => {
    if (!craData?.data?.data) return;
    const cra = craData.data.data;
    setCommentaire(cra.commentaire || "");
    setLineData(cra.lines || {});
    setLineDisabled(
      Object.keys(cra.lines || {}).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );

    // Jours du mois
    const days = [];
    const date = new Date(cra.year, cra.month - 1, 1);
    while (date.getMonth() === cra.month - 1) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    setDaysInMonth(days);

    // Fériés
    const fetchHolidays = async () => {
      try {
        const res = await axios.get(
          `https://date.nager.at/api/v3/PublicHolidays/${cra.year}/FR`
        );
        setHolidays(res.data.map((h) => new Date(h.date).toDateString()));
      } catch (err) {
        console.error(err);
      }
    };
    fetchHolidays();
  }, [craData]);

  // Filtrer projets actifs pour le mois courant
  useEffect(() => {
    if (
      !projects.length ||
      currentMonth === undefined ||
      currentYear === undefined
    )
      return;

    const filtered = projects.filter((p) => {
      if (!p.startDate) return false;
      const start = new Date(p.startDate);
      const end = p.endDate ? new Date(p.endDate) : new Date();
      const currentMonthYear = currentYear * 12 + currentMonth;
      const startMonthYear = start.getFullYear() * 12 + start.getMonth();
      const endMonthYear = end.getFullYear() * 12 + end.getMonth();
      return (
        currentMonthYear >= startMonthYear && currentMonthYear <= endMonthYear
      );
    });

    setFilteredProjects(filtered);
  }, [projects, currentMonth, currentYear]);

  if (isLoading) return <div>Chargement...</div>;
  if (isError || !craData?.data?.data)
    return <div>Erreur lors du chargement du CRA</div>;

  const cra = craData.data.data;

  // Gestion lignes
  const handleInputChange = (lineKey, dayStr, value) => {
    if (!["", "0", "0.", "0.5", "1"].includes(value)) return;
    setLineData((prev) => ({
      ...prev,
      [lineKey]: { ...prev[lineKey], [dayStr]: value },
    }));
  };
  const handleSaveLine = (lineKey) =>
    setLineDisabled((prev) => ({ ...prev, [lineKey]: true }));
  const handleEditLine = (lineKey) =>
    setLineDisabled((prev) => ({ ...prev, [lineKey]: false }));

  // Calculs totaux
  const calculateTotal = (lineKey) => {
    if (!lineData[lineKey]) return 0;
    return Object.entries(lineData[lineKey]).reduce((sum, [dayStr, value]) => {
      const day = new Date(dayStr);
      const isWeekend = day.getDay() === 0 || day.getDay() === 6;
      const isHoliday = holidays.includes(day.toDateString());
      if (isWeekend || isHoliday) return sum;
      return sum + (Number(value) || 0);
    }, 0);
  };
  const calculateColumnTotal = (dayStr) => {
    const day = new Date(dayStr);
    const isWeekend = day.getDay() === 0 || day.getDay() === 6;
    const isHoliday = holidays.includes(day.toDateString());
    if (isWeekend || isHoliday) return 1;
    return Object.keys(lineData).reduce(
      (sum, key) => sum + (Number(lineData[key][dayStr]) || 0),
      0
    );
  };

  const totalProduction = Object.keys(lineData)
    .filter((k) => !["conge_paye", "conge_non_paye", "intern"].includes(k))
    .reduce((sum, k) => sum + calculateTotal(k), 0);
  const totalAbsences = ["conge_paye", "conge_non_paye"].reduce(
    (sum, k) => sum + calculateTotal(k),
    0
  );
  const totalIntern = lineData["intern"] ? calculateTotal("intern") : 0;
  const totalWeekends = daysInMonth.filter(
    (d) => d.getDay() === 0 || d.getDay() === 6
  ).length;
  const totalHolidays = daysInMonth.filter((d) =>
    holidays.includes(d.toDateString())
  ).length;
  const totalDaysInMonth = daysInMonth.length;

  // Ajouter ligne depuis select
  const handleSelectChange = (e) => {
    const value = e.target.value;
    setSelectedOption(value);
    if (!value) return;
    if (lineData[value]) {
      setLineDisabled((prev) => ({ ...prev, [value]: false }));
    } else {
      const newLine = {};
      daysInMonth.forEach((day) => {
        const dayStr = day.toLocaleDateString("fr-CA");
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        const isHoliday = holidays.includes(day.toDateString());
        newLine[dayStr] = isWeekend || isHoliday ? 1 : "";
      });
      setLineData((prev) => ({ ...prev, [value]: newLine }));
      setLineDisabled((prev) => ({ ...prev, [value]: false }));
    }
  };

  // Enregistrement CRA
  const handleSaveCRA = async () => {
    try {
      const formData = new FormData();
      formData.append("lines", JSON.stringify(lineData));
      formData.append("commentaire", commentaire);
      files.forEach((file) => formData.append("attachments", file));
      await updateCRA({ craId, formData }).unwrap();
      alert("CRA mis à jour ✅");
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'enregistrement ❌");
    }
  };

  // Status Badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "draft":
        return <Badge bg="secondary">Brouillon</Badge>;
      case "pending_validation":
        return <Badge bg="warning">En attente</Badge>;
      case "validated":
        return <Badge bg="success">Validé</Badge>;
      case "refused":
        return <Badge bg="danger">Refusé</Badge>;
      default:
        return <Badge bg="dark">Inconnu</Badge>;
    }
  };

  return (
    <div className="timesheet-container p-3 m-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>
          Édition CRA - {cra.month}/{cra.year} {getStatusBadge(cra.status)}
        </h5>
        <div>
          {!isEnvoyer && (
            <Button
              variant="primary"
              onClick={handleSaveCRA}
              disabled={isSaving}
              className="me-2">
              {isSaving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          )}

          <Button
            variant="info"
            disabled={sendingValidator}
            onClick={async () => {
              try {
                await sendToValidator({ craId }).unwrap();
                alert("CRA envoyé au validateur ✅");
                setIsEnvoyer(true);
              } catch (err) {
                console.error(err);
                alert("Erreur lors de l'envoi ❌");
              }
            }}>
            Envoyer au validateur
          </Button>
        </div>
      </div>

      {/* Affichage raison si refus */}
      {cra.status === "refused" && cra.refusalReason && (
        <Alert variant="danger">
          <strong>Motif du refus :</strong> {cra.refusalReason}
        </Alert>
      )}

      {/* Sélecteur projets */}
      <Form.Select
        value={selectedOption}
        onChange={handleSelectChange}
        className="mb-3">
        <option value="">-- Sélectionnez --</option>
        <option value="conge_paye">Congé payé</option>
        <option value="conge_non_paye">Congé non payé</option>
        <option value="intern">Intern</option>
        {filteredProjects.map((p) => (
          <option key={p._id} value={p.title}>
            {p.title}
          </option>
        ))}
      </Form.Select>

      {/* Tableau CRA */}
      <Table bordered hover responsive className="timesheet-table mb-3">
        <thead>
          <tr>
            <th>Jour</th>
            {daysInMonth.map((day) => (
              <th
                key={day.toDateString()}
                className={
                  day.getDay() === 0 || day.getDay() === 6 ? "weekend" : ""
                }>
                <div>{weekDays[day.getDay()]}</div>
                <div>{day.getDate()}</div>
              </th>
            ))}
            <th>Total</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {Object.keys(lineData).map((lineKey) => (
            <tr
              key={lineKey}
              className={lineDisabled[lineKey] ? "saved-line" : ""}>
              <td>{lineKey}</td>
              {daysInMonth.map((day) => {
                const dayStr = day.toLocaleDateString("fr-CA");
                const isWeekend = day.getDay() === 0 || day.getDay() === 6;
                const isHoliday = holidays.includes(day.toDateString());
                return (
                  <td key={dayStr}>
                    <Form.Control
                      type="text"
                      value={lineData[lineKey][dayStr] ?? ""}
                      disabled={lineDisabled[lineKey] || isWeekend || isHoliday}
                      onChange={(e) =>
                        handleInputChange(lineKey, dayStr, e.target.value)
                      }
                    />
                  </td>
                );
              })}
              <td>{calculateTotal(lineKey)}j</td>
              <td>
                {!lineDisabled[lineKey] ? (
                  <Button
                    size="sm"
                    variant="success"
                    onClick={() => handleSaveLine(lineKey)}>
                    💾
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => handleEditLine(lineKey)}>
                    ✏️
                  </Button>
                )}
              </td>
            </tr>
          ))}

          {/* Ligne totaux par jour */}
          <tr className="table-primary font-weight-bold">
            <td>Total</td>
            {daysInMonth.map((day) => {
              const dayStr = day.toLocaleDateString("fr-CA");
              const colTotal = calculateColumnTotal(dayStr);
              return (
                <td
                  key={dayStr}
                  style={{ color: colTotal > 1 ? "red" : "inherit" }}>
                  {colTotal}
                </td>
              );
            })}
            <td>{totalProduction}j</td>
            <td></td>
          </tr>
        </tbody>
      </Table>

      {/* Totaux partiels */}
      <Row className="mb-3">
        <Col>
          <div className="totals-box">
            <strong>Production</strong>
            <div>{totalProduction}j</div>
          </div>
        </Col>
        <Col>
          <div className="totals-box">
            <strong>Absences</strong>
            <div>{totalAbsences}j</div>
          </div>
        </Col>
        <Col>
          <div className="totals-box">
            <strong>Intern</strong>
            <div>{totalIntern}j</div>
          </div>
        </Col>
      </Row>

      {/* Résumé global */}
      <Row className="mb-3">
        <Col>
          <div className="totals-box">
            <strong>Résumé global</strong>
            <div>Total jours : {totalDaysInMonth}</div>
            <div>Production : {totalProduction}j</div>
            <div>Absences : {totalAbsences}j</div>
            <div>Intern : {totalIntern}j</div>
            <div>Weekends : {totalWeekends}j</div>
            <div>Fériés : {totalHolidays}j</div>
          </div>
        </Col>
      </Row>

      {/* Commentaire et fichiers */}
      <Row>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Commentaires</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Documents joints</Form.Label>
            <Form.Control
              type="file"
              multiple
              onChange={(e) => setFiles([...e.target.files])}
            />
          </Form.Group>
        </Col>
      </Row>
      <div className="p-4">
        <Validations steps={steps} currentStep={2} />
      </div>
    </div>
  );
};

export default EditCra;
